import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Button, MenuItem, TextField, Grid, Pagination, Avatar, Stack,
  Snackbar, Alert, IconButton, ToggleButtonGroup, ToggleButton, DialogActions, Tabs, Tab,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CropIcon from '@mui/icons-material/Crop';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PublicIcon from '@mui/icons-material/Public';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import Loader from './Loader';
import ImageCropDialog from './ImageCropDialog';
import { color, font, bevel } from '../styles/tokens';

const BLOGS_PER_PAGE = 5;

// The Blog schema has no draft flag — every saved post is either live
// now or scheduled for a future publishDate. Status is derived, not stored.
function blogStatus(blog) {
  if (blog.publishDate && new Date(blog.publishDate) > new Date()) {
    return { label: 'Scheduled', c: color.cyan, cDim: color.cyanDim, glow: color.cyanGlow };
  }
  return { label: 'Live', c: color.teal, cDim: color.tealDim, glow: color.tealGlow };
}

function authorId(blog) {
  return typeof blog.author === 'object' ? blog.author?._id : blog.author;
}

function isMine(blog, user) {
  if (!user) return false;
  const id = authorId(blog);
  return id === user.id || id === user._id;
}

// Editing, deleting, hiding, and duplicating another author's post is
// restricted to superadmins — everyone else may only act on their own.
function canManage(blog, user) {
  if (!user) return false;
  return user.role === 'superadmin' || isMine(blog, user);
}

function authorName(blog, authorMap) {
  return typeof blog.author === 'object'
    ? blog.author.username
    : authorMap[blog.author] || 'Unknown';
}

function StatusChip({ status }) {
  return (
    <Box sx={{
      fontFamily: font.mono, fontSize: '.625rem', letterSpacing: '.1em', textTransform: 'uppercase',
      px: '.55em', py: '.3em', border: `1px solid ${status.cDim}`, color: status.c,
      background: `color-mix(in srgb, ${status.c} 14%, ${color.panel})`,
      boxShadow: status.label === 'Live' ? `0 0 12px ${status.glow}` : 'none',
      display: 'inline-block', flex: 'none',
    }}>
      {status.label}
    </Box>
  );
}

function HiddenChip() {
  return (
    <Box sx={{
      fontFamily: font.mono, fontSize: '.625rem', letterSpacing: '.1em', textTransform: 'uppercase',
      px: '.55em', py: '.3em', border: `1px solid ${color.boneFaint}`, color: color.boneFaint,
      display: 'inline-block', flex: 'none',
    }}>
      Hidden
    </Box>
  );
}

function BlogList({ user, websites = [], blogs: propBlogs }) {
  const [blogs, setBlogs] = useState(propBlogs || []);
  const [authorMap, setAuthorMap] = useState({});
  const [dispatchTab, setDispatchTab] = useState('all');
  const [selectedWebsite, setSelectedWebsite] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [editingBlog, setEditingBlog] = useState(null);
  const [loading, setLoading] = useState(!propBlogs);
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState(null);
  const [editorFullScreen, setEditorFullScreen] = useState(false);
  const [cropTarget, setCropTarget] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showAll, setShowAll] = useState(false);
  const [jumpValue, setJumpValue] = useState('');
  const [previewBlog, setPreviewBlog] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateWebsite, setDuplicateWebsite] = useState('');

  const fetchBlogs = useCallback(() => {
    if (propBlogs) { setBlogs(propBlogs); return Promise.resolve(); }
    if (!user) return Promise.resolve();
    const token = localStorage.getItem('token');
    return axios.get('/api/blogs/all', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setBlogs(res.data))
      .catch(err => setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t load dispatches. Please try again.' }));
  }, [user, propBlogs]);

  useEffect(() => {
    const file = editingBlog?.newImage;
    if (!file) { setNewImagePreview(null); return; }
    const url = URL.createObjectURL(file);
    setNewImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [editingBlog?.newImage]);

  useEffect(() => {
    setLoading(true);
    fetchBlogs().finally(() => setLoading(false));
  }, [fetchBlogs]);

  // Fetch usernames for author IDs not populated
  useEffect(() => {
    const missingIds = blogs
      .map(blog => (typeof blog.author === 'string' ? blog.author : null))
      .filter(Boolean)
      .filter((id, idx, arr) => arr.indexOf(id) === idx && !authorMap[id]);

    if (missingIds.length) {
      const token = localStorage.getItem('token');
      Promise.all(
        missingIds.map(id =>
          axios.get(`/api/admin/users/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => ({ id, username: res.data.username }))
            .catch(() => ({ id, username: 'Unknown' }))
        )
      ).then(results => {
        const newMap = {};
        results.forEach(({ id, username }) => { newMap[id] = username; });
        setAuthorMap(prev => ({ ...prev, ...newMap }));
      });
    }
  }, [blogs, authorMap]);

  // --- Filtering ---
  const tabBlogs = dispatchTab === 'all'
    ? blogs
    : blogs.filter(blog => dispatchTab === 'mine' ? isMine(blog, user) : !isMine(blog, user));

  const filteredBlogs = tabBlogs.filter(blog => {
    let websiteMatch = true;
    let dateMatch = true;
    let searchMatch = true;
    if (selectedWebsite) {
      const blogWebsiteId = typeof blog.website === 'object' ? blog.website._id : blog.website;
      websiteMatch = blogWebsiteId === selectedWebsite;
    }
    if (dateFrom || dateTo) {
      const blogDate = new Date(blog.createdAt).toISOString().slice(0, 10);
      if (dateFrom) dateMatch = dateMatch && blogDate >= dateFrom;
      if (dateTo) dateMatch = dateMatch && blogDate <= dateTo;
    }
    if (search) {
      const s = search.toLowerCase();
      searchMatch =
        blog.title.toLowerCase().includes(s) ||
        (blog.content && blog.content.toLowerCase().includes(s)) ||
        (blog.website?.name && blog.website.name.toLowerCase().includes(s));
    }
    return websiteMatch && dateMatch && searchMatch;
  });

  // Filters changing invalidates whatever page/jump value was in place.
  useEffect(() => { setPage(1); setJumpValue(''); }, [selectedWebsite, dateFrom, dateTo, search, dispatchTab]);

  // --- Pagination ---
  const pageCount = Math.max(1, Math.ceil(filteredBlogs.length / BLOGS_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const pagedBlogs = showAll
    ? filteredBlogs
    : filteredBlogs.slice((safePage - 1) * BLOGS_PER_PAGE, safePage * BLOGS_PER_PAGE);

  const handleJump = (e) => {
    e.preventDefault();
    const n = parseInt(jumpValue, 10);
    if (!n || n < 1 || n > pageCount) {
      setNotice({ severity: 'error', message: `Enter a page between 1 and ${pageCount}.` });
      return;
    }
    setPage(n);
  };

  // --- Handlers ---
  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/api/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(blogs => blogs.filter(blog => blog._id !== blogId));
      setNotice({ severity: 'success', message: 'Blog deleted.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t delete this blog. Please try again.' });
    }
  };

  const handleEdit = (blog) => { setEditingBlog(blog); setEditorFullScreen(false); setCropTarget(null); setPreviewBlog(null); };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingBlog.title.trim() || !editingBlog.content.trim()) {
      setNotice({ severity: 'error', message: 'Title and content can’t be empty.' });
      return;
    }
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('title', editingBlog.title);
    formData.append('content', editingBlog.content);
    formData.append('website', editingBlog.website._id || editingBlog.website);
    if (editingBlog.publishDate) formData.append('publishDate', editingBlog.publishDate);
    if (editingBlog.newImage) formData.append('image', editingBlog.newImage);

    try {
      await axios.put(`/api/blogs/${editingBlog._id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingBlog(null);
      setNotice({ severity: 'success', message: 'Blog updated.' });
      if (propBlogs) {
        window.location.reload();
      } else {
        await fetchBlogs();
      }
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t save changes. Please try again.' });
    }
  };

  const handleToggleHidden = async (blog) => {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(`/api/blogs/${blog._id}/visibility`, { hidden: !blog.hidden }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(bs => bs.map(b => b._id === blog._id ? { ...b, hidden: !blog.hidden } : b));
      setNotice({ severity: 'success', message: blog.hidden ? 'Dispatch is live on the public feed again.' : 'Dispatch hidden from the public feed.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t change visibility. Please try again.' });
    }
  };

  const openDuplicate = (blog) => {
    setDuplicateTarget(blog);
    setDuplicateWebsite(typeof blog.website === 'object' ? blog.website._id : blog.website);
  };

  const handleDuplicateConfirm = async () => {
    if (!duplicateTarget) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(`/api/blogs/${duplicateTarget._id}/duplicate`, { website: duplicateWebsite }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicateTarget(null);
      setNotice({ severity: 'success', message: 'Duplicated — the copy is hidden from the public feed until you review and unhide it.' });
      await fetchBlogs();
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t duplicate this dispatch. Please try again.' });
    }
  };

  const renderActions = (blog, size = 'small') => (
    <Box sx={{ display: 'flex', gap: 0.5, flex: 'none' }}>
      <IconButton size={size} title="Preview" onClick={() => setPreviewBlog(blog)} sx={{ color: color.boneDim, '&:hover': { color: color.cyan } }}>
        <VisibilityIcon fontSize={size} />
      </IconButton>
      {canManage(blog, user) && (
        <>
          <IconButton size={size} title="Edit" onClick={() => handleEdit(blog)} sx={{ color: color.boneDim, '&:hover': { color: color.cyan } }}>
            <EditIcon fontSize={size} />
          </IconButton>
          <IconButton
            size={size}
            title={blog.hidden ? 'Show on public feed' : 'Hide from public feed'}
            onClick={() => handleToggleHidden(blog)}
            sx={{ color: color.boneDim, '&:hover': { color: color.amber } }}
          >
            {blog.hidden ? <PublicOffIcon fontSize={size} /> : <PublicIcon fontSize={size} />}
          </IconButton>
          <IconButton size={size} title="Duplicate" onClick={() => openDuplicate(blog)} sx={{ color: color.boneDim, '&:hover': { color: color.cyan } }}>
            <ContentCopyIcon fontSize={size} />
          </IconButton>
          <IconButton size={size} title="Delete" onClick={() => handleDelete(blog._id)} sx={{ color: color.boneDim, '&:hover': { color: color.red } }}>
            <CloseIcon fontSize={size} />
          </IconButton>
        </>
      )}
    </Box>
  );

  const renderGridCard = (blog) => {
    const status = blogStatus(blog);
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={blog._id}>
        <Paper sx={{
          display: 'flex', flexDirection: 'column', height: '100%', p: 0,
          background: color.panel, color: color.boneDim, border: `1px solid ${color.ruleSoft}`,
          clipPath: bevel(10), overflow: 'hidden', position: 'relative', opacity: blog.hidden ? 0.75 : 1,
          transition: 'transform .15s ease, border-color .15s ease',
          '&:hover': { transform: 'translateY(-3px)', borderColor: color.cyanDim },
        }}>
          <Box sx={{
            height: blog.imageUrl ? 160 : 40, position: 'relative',
            background: blog.imageUrl ? undefined : `linear-gradient(135deg, ${color.panel2}, ${color.ink})`,
          }}>
            {blog.imageUrl && (
              <Box
                component="img"
                src={blog.imageUrl}
                alt={blog.title}
                onClick={() => setLightboxSrc(blog.imageUrl)}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
              />
            )}
            <Box sx={{ position: 'absolute', top: '.6em', left: '.6em', display: 'flex', gap: '.4em' }}>
              <StatusChip status={status} />
              {blog.hidden && <HiddenChip />}
            </Box>
          </Box>
          <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: '.5em' }}>
            <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', letterSpacing: '.08em', textTransform: 'uppercase', color: color.cyan }}>
              {blog.website?.name || blog.website || 'No property'}
            </Typography>
            <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '1.05rem', color: color.bone, lineHeight: 1.3 }}>
              {blog.title}
            </Typography>
            <Typography variant="body2" sx={{
              color: color.boneDim, overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 40
            }}>
              {blog.content?.replace(/<[^>]+>/g, '').slice(0, 120)}...
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 'auto', pt: 1, borderTop: `1px dashed ${color.ruleSoft}` }}>
              {blog.author && (
                <Avatar sx={{ width: 26, height: 26, bgcolor: color.cyanDim, color: color.ink, fontWeight: 700, fontSize: '.75rem' }}>
                  {(typeof blog.author === 'object' ? blog.author.username?.[0] : authorMap[blog.author]?.[0] || 'U')?.toUpperCase()}
                </Avatar>
              )}
              <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', color: color.boneDim }}>
                {authorName(blog, authorMap)}
              </Typography>
              <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', color: color.boneFaint, ml: 'auto' }}>
                {blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : 'N/A'}
              </Typography>
            </Stack>
            <Box sx={{ mt: 1 }}>{renderActions(blog)}</Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderListRow = (blog) => {
    const status = blogStatus(blog);
    return (
      <Paper key={blog._id} sx={{
        display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1.5, opacity: blog.hidden ? 0.75 : 1,
        background: color.panel, border: `1px solid ${color.ruleSoft}`, clipPath: bevel(8),
      }}>
        {blog.imageUrl ? (
          <Box
            component="img"
            src={blog.imageUrl}
            alt={blog.title}
            onClick={() => setLightboxSrc(blog.imageUrl)}
            sx={{ width: 96, height: 72, objectFit: 'cover', flex: 'none', cursor: 'pointer', border: `1px solid ${color.ruleSoft}` }}
          />
        ) : (
          <Box sx={{ width: 96, height: 72, flex: 'none', background: `linear-gradient(135deg, ${color.panel2}, ${color.ink})` }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: font.mono, fontSize: '.625rem', letterSpacing: '.08em', textTransform: 'uppercase', color: color.cyan }}>
            {blog.website?.name || blog.website || 'No property'}
          </Typography>
          <Typography noWrap sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '.95rem', color: color.bone }}>
            {blog.title}
          </Typography>
          <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', color: color.boneFaint }}>
            {authorName(blog, authorMap)} &middot; {blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '.4em' }}>
          <StatusChip status={status} />
          {blog.hidden && <HiddenChip />}
        </Box>
        {renderActions(blog)}
      </Paper>
    );
  };

  return (
    <Box sx={{ minHeight: '60vh' }}>
      <Tabs
        value={dispatchTab}
        onChange={(_, v) => setDispatchTab(v)}
        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 1 } }}
      >
        <Tab value="all" label="All" />
        <Tab value="mine" label="Created by me" />
        <Tab value="others" label="Others" />
      </Tabs>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box sx={{
            display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'flex-end',
            justifyContent: 'space-between', pb: 2, borderBottom: `1px solid ${color.ruleSoft}`,
          }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <TextField
                select
                label="Property"
                value={selectedWebsite}
                onChange={e => setSelectedWebsite(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All properties</MenuItem>
                {websites.map(site => (
                  <MenuItem key={site._id} value={site._id}>{site.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Filed from"
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Filed to"
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Search the wire"
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ minWidth: 200 }}
              />
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              onChange={(_, v) => v && setViewMode(v)}
            >
              <ToggleButton value="grid" title="Grid view"><GridViewIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="list" title="List view"><ViewListIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {pagedBlogs.length === 0 ? (
            <Paper sx={{ p: 3, background: color.panel, border: `1px solid ${color.ruleSoft}`, clipPath: bevel(10), color: color.boneDim }}>
              <Typography sx={{ fontWeight: 700, textAlign: 'center' }}>
                {dispatchTab === 'all' ? 'No dispatches found' : dispatchTab === 'mine' ? 'You haven’t created any dispatches yet' : 'No dispatches from others found'}
              </Typography>
            </Paper>
          ) : viewMode === 'grid' ? (
            <Grid container spacing={3}>
              {pagedBlogs.map(renderGridCard)}
            </Grid>
          ) : (
            <Box>{pagedBlogs.map(renderListRow)}</Box>
          )}

          {/* Pagination */}
          {filteredBlogs.length > BLOGS_PER_PAGE && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4 }}>
              {!showAll && (
                <>
                  <Pagination
                    count={pageCount}
                    page={safePage}
                    onChange={(_, value) => setPage(value)}
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': { color: color.boneDim, borderColor: color.rule, fontFamily: font.mono },
                      '& .Mui-selected': { backgroundColor: `${color.cyanDim} !important`, color: `${color.ink} !important` },
                    }}
                  />
                  <Box component="form" onSubmit={handleJump} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Jump to"
                      value={jumpValue}
                      onChange={e => setJumpValue(e.target.value)}
                      sx={{ width: 90 }}
                      inputProps={{ min: 1, max: pageCount }}
                    />
                    <Button type="submit" size="small" variant="outlined">Go</Button>
                  </Box>
                </>
              )}
              <Button size="small" variant={showAll ? 'contained' : 'outlined'} color="primary" onClick={() => setShowAll(s => !s)}>
                {showAll ? 'Paginate' : `View all ${filteredBlogs.length}`}
              </Button>
            </Box>
          )}

          {/* Edit Dialog */}
          <Dialog
            open={!!editingBlog}
            onClose={() => setEditingBlog(null)}
            maxWidth="md"
            fullWidth
            fullScreen={editorFullScreen}
          >
            <DialogTitle sx={{ fontFamily: font.display, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Edit dispatch
              <IconButton
                onClick={() => setEditorFullScreen(f => !f)}
                title={editorFullScreen ? 'Exit fullscreen' : 'Expand to fullscreen'}
                sx={{ color: color.boneDim, '&:hover': { color: color.cyan } }}
              >
                {editorFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </DialogTitle>
            <DialogContent sx={editorFullScreen ? { display: 'flex', flexDirection: 'column' } : undefined}>
              {editingBlog && (
                <form
                  onSubmit={handleEditSubmit}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8,
                    flex: editorFullScreen ? 1 : 'none', minHeight: 0,
                  }}
                >
                  <TextField
                    label="Title"
                    value={editingBlog.title}
                    onChange={e => setEditingBlog({ ...editingBlog, title: e.target.value })}
                    required
                  />
                  <Box sx={editorFullScreen ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : undefined}>
                    <Typography sx={{ fontFamily: font.mono, fontSize: '.625rem', letterSpacing: '.1em', textTransform: 'uppercase', color: color.boneFaint, mb: 1 }}>
                      Content
                    </Typography>
                    <Box
                      sx={{
                        background: color.panel,
                        border: `1px solid ${color.ruleSoft}`,
                        clipPath: bevel(10),
                        overflow: 'hidden',
                        display: editorFullScreen ? 'flex' : 'block',
                        flexDirection: 'column',
                        flex: editorFullScreen ? 1 : 'none',
                        minHeight: 0,
                        '& .ql-toolbar': { borderRadius: 0, border: 'none', borderBottom: `1px solid ${color.ruleSoft}`, background: color.panel2, flex: 'none' },
                        '& .ql-container': editorFullScreen
                          ? { flex: 1, overflow: 'auto', background: '#fff', color: '#1d1e1f', border: 'none' }
                          : { minHeight: 220, maxHeight: 520, resize: 'vertical', overflow: 'auto', background: '#fff', color: '#1d1e1f', border: 'none' },
                        '& .ql-editor': { fontSize: 16 },
                      }}
                    >
                      <ReactQuill value={editingBlog.content} onChange={value => setEditingBlog({ ...editingBlog, content: value })} />
                    </Box>
                  </Box>
                  <TextField
                    select
                    label="Website"
                    value={editingBlog.website._id || editingBlog.website}
                    onChange={e => setEditingBlog({ ...editingBlog, website: e.target.value })}
                    required
                  >
                    {websites.map(w => (
                      <MenuItem key={w._id} value={w._id}>{w.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Publish Date"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={editingBlog.publishDate ? editingBlog.publishDate.slice(0, 16) : ''}
                    onChange={e => setEditingBlog({ ...editingBlog, publishDate: e.target.value })}
                  />
                  <Box>
                    <Typography sx={{ fontFamily: font.mono, fontSize: '.625rem', letterSpacing: '.1em', textTransform: 'uppercase', color: color.boneFaint, mb: 1 }}>
                      Attached art
                    </Typography>
                    {(newImagePreview || editingBlog.imageUrl) ? (
                      <Box>
                        <Box
                          component="img"
                          src={newImagePreview || editingBlog.imageUrl}
                          alt=""
                          sx={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block', border: `1px solid ${color.ruleSoft}`, mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CropIcon />}
                            onClick={() => setCropTarget(
                              editingBlog.newImage || { url: editingBlog.imageUrl, name: 'image.jpg' }
                            )}
                            sx={{ flex: 1 }}
                          >
                            Edit crop
                          </Button>
                          <Button size="small" variant="outlined" component="label" sx={{ flex: 1 }}>
                            Replace
                            <input type="file" accept="image/*" hidden onChange={e => { if (e.target.files[0]) setCropTarget(e.target.files[0]); e.target.value = ''; }} />
                          </Button>
                          {newImagePreview && (
                            <Button size="small" variant="outlined" color="error" onClick={() => setEditingBlog({ ...editingBlog, newImage: null })} title="Revert to original image">
                              <CloseIcon fontSize="small" />
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Button variant="outlined" component="label" fullWidth>
                        Upload image
                        <input type="file" accept="image/*" hidden onChange={e => { if (e.target.files[0]) setCropTarget(e.target.files[0]); e.target.value = ''; }} />
                      </Button>
                    )}
                  </Box>
                  <Button type="submit" variant="contained" color="primary">
                    Save changes
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>

          {/* Preview Dialog */}
          <Dialog open={!!previewBlog} onClose={() => setPreviewBlog(null)} maxWidth="sm" fullWidth>
            {previewBlog && (
              <>
                <DialogTitle sx={{ fontFamily: font.display, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Preview
                  <IconButton onClick={() => setPreviewBlog(null)} sx={{ color: color.boneDim }}>
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                  {previewBlog.imageUrl && (
                    <Box
                      component="img"
                      src={previewBlog.imageUrl}
                      alt={previewBlog.title}
                      onClick={() => setLightboxSrc(previewBlog.imageUrl)}
                      sx={{ width: '100%', maxHeight: 300, objectFit: 'cover', display: 'block', mb: 2, cursor: 'pointer', border: `1px solid ${color.ruleSoft}` }}
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <StatusChip status={blogStatus(previewBlog)} />
                    {previewBlog.hidden && <HiddenChip />}
                    <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', letterSpacing: '.08em', textTransform: 'uppercase', color: color.cyan }}>
                      {previewBlog.website?.name || previewBlog.website || 'No property'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: font.display, fontWeight: 700, fontSize: '1.4rem', color: color.bone, mb: 1 }}>
                    {previewBlog.title}
                  </Typography>
                  <Typography sx={{ fontFamily: font.mono, fontSize: '.75rem', color: color.boneFaint, mb: 2 }}>
                    {authorName(previewBlog, authorMap)} &middot; {previewBlog.createdAt ? new Date(previewBlog.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                  <Box
                    sx={{
                      color: color.boneDim, lineHeight: 1.7,
                      '& p': { margin: '0 0 1em' },
                      '& h1, & h2, & h3': { color: color.bone, fontFamily: font.display, margin: '1.2em 0 .5em' },
                      '& img': { maxWidth: '100%' },
                      '& a': { color: color.cyan },
                      '& ul, & ol': { paddingLeft: '1.4em' },
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewBlog.content) }}
                  />
                </DialogContent>
                {canManage(previewBlog, user) && (
                  <DialogActions>
                    <Button startIcon={<EditIcon />} onClick={() => handleEdit(previewBlog)} variant="outlined">
                      Edit this dispatch
                    </Button>
                  </DialogActions>
                )}
              </>
            )}
          </Dialog>

          {/* Duplicate Dialog */}
          <Dialog open={!!duplicateTarget} onClose={() => setDuplicateTarget(null)} maxWidth="xs" fullWidth>
            {duplicateTarget && (
              <>
                <DialogTitle sx={{ fontFamily: font.display, fontWeight: 600 }}>Duplicate dispatch</DialogTitle>
                <DialogContent>
                  <Typography sx={{ color: color.boneDim, mb: 2 }}>
                    Create a copy of &ldquo;{duplicateTarget.title}&rdquo;. The copy starts hidden from the public feed until you review and unhide it.
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Duplicate into property"
                    value={duplicateWebsite}
                    onChange={e => setDuplicateWebsite(e.target.value)}
                  >
                    {websites.map(w => (
                      <MenuItem key={w._id} value={w._id}>{w.name}</MenuItem>
                    ))}
                  </TextField>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDuplicateTarget(null)} variant="outlined">Cancel</Button>
                  <Button onClick={handleDuplicateConfirm} variant="contained" color="primary">Duplicate</Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Lightbox */}
          <Dialog open={!!lightboxSrc} onClose={() => setLightboxSrc(null)} maxWidth="lg">
            <Box sx={{ position: 'relative', background: color.ink, lineHeight: 0 }}>
              <IconButton
                onClick={() => setLightboxSrc(null)}
                sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', background: 'rgba(0,0,0,.5)', '&:hover': { background: 'rgba(0,0,0,.7)' } }}
              >
                <CloseIcon />
              </IconButton>
              {lightboxSrc && (
                <Box component="img" src={lightboxSrc} alt="" sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
              )}
            </Box>
          </Dialog>
        </>
      )}
      <Snackbar
        open={!!notice}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert> : undefined}
      </Snackbar>
      <ImageCropDialog
        open={!!cropTarget}
        source={cropTarget}
        onCancel={() => setCropTarget(null)}
        onApply={croppedFile => { setEditingBlog(prev => ({ ...prev, newImage: croppedFile })); setCropTarget(null); }}
        onError={message => setNotice({ severity: 'error', message })}
      />
    </Box>
  );
}

export default BlogList;
